import axios from "axios";
import { debug, formatSafeErrorMessage } from "../../../utils/debugLogger";
import { createOAuthState, type OAuthStateOptions } from "../../../utils/oauthState";
import {
  GHL_SCOPES,
  getGHLClientId,
  getGHLRedirectUri,
  getGHLVersionId,
} from "../config/ghlEnv";
import type { GHLExchangeTokenResponse } from "../types/ghl.types";

/**
 * GoHighLevel's consent screen. "chooselocation" is the sub-account picker: the
 * user selects which of their GHL locations to link, and that choice comes back
 * as `locationId` on the token response. There is no agency-level variant we can
 * use — every scope this app requests is Sub-Account level.
 *
 * The `/v2/` prefix is required, not cosmetic. The v1 path resolves the app's
 * published version implicitly and returns
 *   error.noAppVersionIdFound
 *   HttpException: No integration found with the id: <appId>
 * for an app that has no published version — i.e. for every app still in Draft,
 * which is how this one is tested before it goes to the marketplace. v2 takes
 * `version_id` explicitly. Confirmed 2026-08-16 against HighLevel's own
 * generated install link, which uses exactly this URL shape.
 */
const GHL_AUTHORIZE_BASE = "https://marketplace.gohighlevel.com/v2/oauth/chooselocation";

const SOCIAL_API_BASE =
  process.env.REACT_APP_SOCIAL_API_URL ||
  "https://bc631mqtg1.execute-api.us-east-1.amazonaws.com/Prod";

/**
 * GHL's OAuth state must survive a change of tab. Its paid-app install completes
 * inside a marketplace modal and then opens the redirect URL in a NEW tab, so a
 * tab-scoped (sessionStorage) nonce is gone by the time GHLCallback runs. The
 * cross-tab option puts it in localStorage instead — shared across tabs of this
 * origin, still origin-scoped and still single-use. `getGHLAuthorizeUrl` and
 * `GHLCallback` both import this so the create/validate stores cannot diverge.
 */
export const GHL_OAUTH_STATE_OPTIONS: OAuthStateOptions = { crossTab: true };
export const GHL_OAUTH_STATE_PROVIDER = "ghl";

export { getGHLRedirectUri };

/**
 * The backend's user-facing message for a failed request, or `fallback`.
 *
 * Same field precedence as getApiErrorMessage in services/socialTokenService, so
 * a GHL failure reads the way every other platform's does — but deliberately
 * re-stated here instead of imported. That module's import chain reaches
 * postService, which calls getApiBaseUrl() at module scope and throws when
 * REACT_APP_CONTENT_API_URL is unset. This file is loaded by the public
 * /connect/crm/callback route, which must not fail to load because the unrelated
 * content API is unconfigured. Four lines of duplication is the cheaper trade.
 */
const apiErrorMessage = (error: unknown, fallback: string): string => {
  const err = error as {
    response?: { data?: { message?: string; error?: string } };
    message?: string;
  };
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback
  );
};

/**
 * Build the GoHighLevel authorization URL.
 *
 * Mirrors getWordPressAuthorizeUrl() — the frontend constructs the consent URL
 * and the backend owns only the code exchange.
 */
export const getGHLAuthorizeUrl = (): string => {
  const clientId = getGHLClientId();
  if (!clientId?.trim()) {
    throw new Error(
      "REACT_APP_GHL_CLIENT_ID is not set. Add it to .env.local and restart."
    );
  }
  const versionId = getGHLVersionId();
  if (!versionId?.trim()) {
    throw new Error(
      "REACT_APP_GHL_VERSION_ID could not be resolved. Set it to the marketplace " +
        "app's version id and restart."
    );
  }
  const params = new URLSearchParams({
    response_type: "code",
    client_id: clientId,
    redirect_uri: getGHLRedirectUri(),
    scope: GHL_SCOPES.join(" "),
    version_id: versionId,
    state: createOAuthState(GHL_OAUTH_STATE_PROVIDER, GHL_OAUTH_STATE_OPTIONS),
  });
  return `${GHL_AUTHORIZE_BASE}?${params.toString()}`;
};

/**
 * Send the browser to GoHighLevel to begin the consent flow.
 *
 * The caller must stash the Cognito token first (see handleGoHighLevelConnect in
 * ConnectPage) — the redirect tears down React context, and the callback route
 * is public so it cannot rely on AuthContext being rehydrated.
 */
export const initiateGHLOAuth = (): void => {
  window.location.href = getGHLAuthorizeUrl();
};

/**
 * Exchange the GoHighLevel authorization code for tokens via the backend.
 *
 * The code never becomes a credential in the browser: social-management does the
 * exchange, stores the access/refresh pair in Secrets Manager and returns only
 * the location metadata. That is also what binds the connection to a tenant —
 * the backend requires this caller to own `customerId`, so an intercepted code
 * cannot be planted into someone else's account.
 */
export const exchangeGHLToken = async (
  code: string,
  customerId: string,
  idToken: string,
  redirectUri?: string
): Promise<GHLExchangeTokenResponse> => {
  if (!code?.trim()) throw new Error("Authorization code is required");
  if (!customerId?.trim()) {
    throw new Error("Customer ID is required for GoHighLevel token exchange");
  }
  if (!idToken?.trim()) throw new Error("Authentication token is required");

  const resolvedRedirectUri = redirectUri?.trim() || getGHLRedirectUri();
  const endpoint = `${SOCIAL_API_BASE}/${customerId}/ghl/exchange-token`;

  debug.log("GHL token exchange: start", { customerId, redirectUri: resolvedRedirectUri });

  const response = await axios.post<GHLExchangeTokenResponse>(
    endpoint,
    { code: code.trim(), redirect_uri: resolvedRedirectUri },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
    }
  );

  if (!response.data?.success) {
    debug.error("GHL token exchange: backend returned success=false", response.data);
    throw new Error(response.data?.message || "GoHighLevel token exchange failed");
  }

  debug.log("GHL token exchange: success", { locationId: response.data.location_id });
  return response.data;
};

/**
 * Disconnect one GoHighLevel sub-account.
 *
 * Deliberately NOT deleteSocialToken (DELETE /{customer_id}/tokens), which every
 * other platform's Disconnect uses. A GHL connection is a sub-account *binding*,
 * not a social token: releasing it has to flip the GHLLocations row to `revoked`
 * as well as delete the credential, and only this endpoint does both. Revoking
 * rather than deleting that row is what lets the same sub-account be connected
 * again afterwards.
 *
 * Also the escape hatch when a location is already linked to another account:
 * the backend refuses a silent takeover, so the previous owner must disconnect
 * before it can be reconnected elsewhere.
 *
 * Failure handling mirrors deleteSocialToken deliberately, so a GHL disconnect
 * reports problems exactly the way a LinkedIn one does. The backend's messages
 * are written to be read by the person clicking the button ("already connected
 * to another account"), so they are surfaced through apiErrorMessage rather than
 * letting axios' "Request failed with status code 404" reach the UI.
 */
export const disconnectGHLLocation = async (
  customerId: string,
  locationId: string,
  idToken: string
): Promise<void> => {
  if (!customerId?.trim()) {
    throw new Error("Customer ID is required to disconnect GoHighLevel");
  }
  if (!locationId?.trim()) {
    throw new Error("Location ID is required to disconnect GoHighLevel");
  }
  if (!idToken?.trim()) throw new Error("Authentication token is required");

  const endpoint = `${SOCIAL_API_BASE}/${customerId}/ghl/disconnect`;

  try {
    await axios.delete(endpoint, {
      params: { location_id: locationId.trim() },
      headers: { Authorization: `Bearer ${idToken}` },
    });
    debug.log("GHL disconnect: success", { customerId, locationId });
  } catch (error: unknown) {
    debug.error("GHL disconnect failed:", formatSafeErrorMessage(error));
    throw new Error(apiErrorMessage(error, "Failed to disconnect GoHighLevel"));
  }
};
